import { useState } from 'react';
import { Box, ButtonGroup, Button, Typography, CircularProgress, IconButton, useMediaQuery, useTheme, Badge, Tooltip } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import proposalsServices from '../../services/proposalsServices';
import SendPaymentEmailModal from '../Proposal/SendPaymentEmailModal';
import SendPaymentRequestModal from '../Proposal/SendPaymentRequestModal';
import { calculateTotalPay } from '../../utils/proposalCalculations';

const fontFamily = '"Inter", sans-serif';

const header_styles = {
  background: 'linear-gradient(135deg, #0d3c61 0%, #1a5a8a 100%)',
  boxShadow: '0 4px 12px rgba(13, 60, 97, 0.3)',
  padding: { xs: '0.5rem 0', md: '1rem 0' },
  position: 'fixed',
  width: '100%',
  zIndex: 10,
  '@media print': {
    display: 'none !important',
    visibility: 'hidden !important',
    position: 'absolute !important',
    left: '-9999px !important',
    width: '0 !important',
    height: '0 !important',
    overflow: 'hidden !important',
  },
};

const container = {
  maxWidth: 950,
  margin: '0 auto',
  px: 2,
  display: 'flex',
  flexWrap: {
    md: 'nowrap',
    xs: 'wrap',
  },
  justifyContent: {
    xs: 'center',
    md: 'space-between',
  },
  alignItems: 'center',
  gap: 2,
};

const buttonStyles = {
  fontFamily,
  textTransform: 'none',
  fontWeight: 500,
};

const ProposalViewHeader = ({
  handlePrint,
  handlePdf,
  handleEmailModalOpen,
  handleSignatureModalOpen,
  isAccepted,
  token,
  totalPay,
  pdfLoading,
  // New props for payment functionality
  proposal,
  onRefresh,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [amountDueModalOpen, setAmountDueModalOpen] = useState(false);
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/proposals');
    }
  };

  const isCompleted = proposal?.status === 'Completed';
  const isPaid = proposal?.additionalNotes?.payment?.completed === true;
  // The ledger can disagree with the legacy paid flag (additionalNotes): when
  // recorded payments leave a real outstanding balance, keep the Send Invoice
  // button visible even for 'paid'-flagged jobs. Legacy jobs with no recorded
  // payments keep the old flag-driven behavior.
  const paymentsTotal = (proposal?.payments || []).reduce((sum, pay) => sum + (Number(pay.amount) || 0), 0);
  const contractTotal = calculateTotalPay(proposal);
  const hasLedgerBalance = (proposal?.payments?.length || 0) > 0 && contractTotal - paymentsTotal > 0.005;
  const amountDueCount = proposal?.amountDueRemindersSent || 0;
  const receiptCount = proposal?.paymentReceiptsSent || 0;

  return (
    <Box component="header" sx={header_styles}>
      <Box sx={container}>
        {/* Back Button - only for authenticated users, not for client view */}
        {!token && (
          <IconButton
            onClick={handleBack}
            sx={{
              color: '#fff',
              mr: { xs: 1, md: 2 },
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)',
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        )}

        {/* Action Buttons */}
        <ButtonGroup 
          variant="contained" 
          size={isMobile ? 'small' : 'medium'}
          sx={{ backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 1 }}
        >
          <Button 
            onClick={handlePdf}
            disabled={pdfLoading}
            startIcon={!isMobile && (pdfLoading ? <CircularProgress size={14} color="inherit" /> : <PictureAsPdfIcon sx={{ fontSize: 16 }} />)}
            sx={{ 
              ...buttonStyles,
              backgroundColor: 'transparent',
              color: '#fff',
              fontSize: { xs: '0.65rem', sm: '0.75rem' },
              px: { xs: 0.75, sm: 1.5 },
              py: { xs: 0.5, sm: 0.75 },
              minWidth: 'auto',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)',
              },
              borderColor: 'rgba(255,255,255,0.3) !important',
            }}
          >
            {isMobile ? (pdfLoading ? <CircularProgress size={12} color="inherit" /> : 'PDF') : 'Download PDF'}
          </Button>
          <Button 
            onClick={handlePrint} 
            startIcon={!isMobile && <PrintIcon sx={{ fontSize: 16 }} />}
            sx={{ 
              ...buttonStyles,
              backgroundColor: 'transparent',
              color: '#fff',
              fontSize: { xs: '0.65rem', sm: '0.75rem' },
              px: { xs: 0.75, sm: 1.5 },
              py: { xs: 0.5, sm: 0.75 },
              minWidth: 'auto',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)',
              },
              borderColor: 'rgba(255,255,255,0.3) !important',
            }}
          >
            Print
          </Button>
          {!token && (
            <Button 
              onClick={handleEmailModalOpen}
              startIcon={!isMobile && <EmailIcon sx={{ fontSize: 16 }} />}
              sx={{ 
                ...buttonStyles,
                backgroundColor: 'transparent',
                color: '#fff',
                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                px: { xs: 0.75, sm: 1.5 },
                py: { xs: 0.5, sm: 0.75 },
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.2)',
                },
                borderColor: 'rgba(255,255,255,0.3) !important',
              }}
            >
              Email
            </Button>
          )}
          
          {/* Send Amount Due - shows when status is Completed */}
          {!token && isCompleted && (!isPaid || hasLedgerBalance) && (
            <Tooltip title={`Send payment request to client${amountDueCount > 0 ? ` (${amountDueCount} reminders sent before)` : ''}`}>
              <Button 
                onClick={() => setAmountDueModalOpen(true)}
                startIcon={!isMobile && <RequestQuoteIcon sx={{ fontSize: 16 }} />}
                sx={{ 
                  ...buttonStyles,
                  backgroundColor: 'rgba(255, 152, 0, 0.3)',
                  color: '#fff',
                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                  px: { xs: 0.75, sm: 1.5 },
                  pr: amountDueCount > 0 ? { xs: 2.5, sm: 3 } : { xs: 0.75, sm: 1.5 },
                  py: { xs: 0.5, sm: 0.75 },
                  minWidth: 'auto',
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 152, 0, 0.5)',
                  },
                  borderColor: 'rgba(255,255,255,0.3) !important',
                }}
              >
                {isMobile ? 'Invoice' : 'Send Invoice'}
                {amountDueCount > 0 && (
                  <Badge 
                    badgeContent={amountDueCount} 
                    color="warning"
                    sx={{ 
                      position: 'absolute',
                      right: 4,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      '& .MuiBadge-badge': {
                        fontSize: 9,
                        minWidth: 16,
                        height: 16,
                        position: 'relative',
                        transform: 'none',
                      }
                    }}
                  />
                )}
              </Button>
            </Tooltip>
          )}

          {/* Send Payment Receipt - shows when status is Paid */}
          {!token && isPaid && (
            <Tooltip title={`Send payment receipt to client${receiptCount > 0 ? ` (${receiptCount} sent)` : ''}`}>
              <Button 
                onClick={() => setReceiptModalOpen(true)}
                startIcon={!isMobile && <ReceiptIcon sx={{ fontSize: 16 }} />}
                sx={{ 
                  ...buttonStyles,
                  backgroundColor: 'rgba(76, 175, 80, 0.5)',
                  color: '#fff',
                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                  px: { xs: 0.75, sm: 1.5 },
                  py: { xs: 0.5, sm: 0.75 },
                  minWidth: 'auto',
                  '&:hover': {
                    backgroundColor: 'rgba(76, 175, 80, 0.7)',
                  },
                  borderColor: 'rgba(255,255,255,0.3) !important',
                }}
              >
                {isMobile ? 'Rcpt' : 'Send Receipt'}
                {receiptCount > 0 && (
                  <Badge 
                    badgeContent={receiptCount} 
                    color="success"
                    sx={{ 
                      ml: 0.5,
                      '& .MuiBadge-badge': {
                        fontSize: 9,
                        minWidth: 14,
                        height: 14,
                      }
                    }}
                  />
                )}
              </Button>
            </Tooltip>
          )}

          {/* Print Receipt - shows when status is Paid */}
          {!token && isPaid && (
            <Tooltip title="Print payment receipt">
              <Button 
                onClick={() => window.open(`/proposals/receipt/${proposal?.id}`, "_blank")}
                startIcon={!isMobile && <PrintIcon sx={{ fontSize: 16 }} />}
                sx={{ 
                  ...buttonStyles,
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  color: "#fff",
                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                  px: { xs: 0.75, sm: 1.5 },
                  py: { xs: 0.5, sm: 0.75 },
                  minWidth: 'auto',
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.3)",
                  },
                  borderColor: "rgba(255,255,255,0.3) !important",
                }}
              >
                {isMobile ? "Print" : "Print Receipt"}
              </Button>
            </Tooltip>
          )}
        </ButtonGroup>

        {/* Total and Accept */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 3 }, ml: 'auto' }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography 
              sx={{ 
                fontFamily,
                fontSize: { xs: 9, sm: 11 }, 
                color: 'rgba(255,255,255,0.7)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Total Contract Price
            </Typography>
            <Typography 
              sx={{ 
                fontFamily,
                fontSize: { xs: 16, sm: 20, md: 24 }, 
                fontWeight: 700,
                color: '#fff',
              }}
            >
              {totalPay?.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
              }) || '$0.00'}
            </Typography>
          </Box>

          {isAccepted ? (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                backgroundColor: 'rgba(76, 175, 80, 0.2)',
                px: { xs: 1, sm: 2 },
                py: { xs: 0.5, sm: 1 },
                borderRadius: 1,
              }}
            >
              <CheckCircleIcon sx={{ color: '#4caf50', fontSize: { xs: 18, sm: 24 } }} />
              {!isMobile && (
                <Typography sx={{ fontFamily, color: '#4caf50', fontWeight: 600 }}>
                  Accepted
                </Typography>
              )}
            </Box>
          ) : (
            <Button
              variant="contained"
              size={isMobile ? 'small' : 'large'}
              onClick={handleSignatureModalOpen}
              disabled={!token}
              sx={{
                fontFamily,
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: '#4caf50',
                color: '#fff',
                px: { xs: 1.5, sm: 3 },
                py: { xs: 0.75, sm: 1.5 },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                '&:hover': {
                  backgroundColor: '#43a047',
                },
                '&:disabled': {
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'rgba(255,255,255,0.5)',
                },
              }}
            >
              {isMobile ? 'Accept' : 'Accept Proposal'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Send Invoice (payment request) Modal — replaces the Amount Due email */}
      <SendPaymentRequestModal
        isOpen={amountDueModalOpen}
        onClose={() => setAmountDueModalOpen(false)}
        proposal={proposal}
        onSuccess={onRefresh}
      />

      {/* Receipt Email Modal */}
      <SendPaymentEmailModal
        isOpen={receiptModalOpen}
        onClose={() => setReceiptModalOpen(false)}
        proposal={proposal}
        type="receipt"
        onSuccess={onRefresh}
      />
    </Box>
  );
};

export default ProposalViewHeader;
